import { useCallback, useEffect, useState } from 'react';
import { bidsApi } from '@/api/bidsApi';
import { authApi } from '@/api/authApi';
import { prefixes, useTypedTranslation } from '@/locales';
import { EmptyState } from "@/components/ui/EmptyState.tsx";
import { Button } from "@/components/_untitled/base/buttons/button.tsx";
import { Link } from "@/components/navigation/Link.tsx";
import { build, ROUTES } from "@/config/routes.ts";
import type { UserBidItem } from "@/types/userBids.ts";
import { BadgeWithDot } from "@/components/_untitled/base/badges/badges.tsx";
import { useWebSocket } from "@/hooks/useWebSocket";
import { cx } from "@/utils/cx.ts";
import { useAuth } from "@/features/auth/hooks/useAuth";

export const MyBidsSlideout = () => {
    const [tBids, tEmptyState, tGeneral] = useTypedTranslation(prefixes('myBids', 'emptyState', 'general'));
    const [bids, setBids] = useState<UserBidItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { on } = useWebSocket({ namespace: '/user' });
    const { refetchUser } = useAuth();

    const fetchBids = useCallback(async () => {
        try {
            setError(null);
            const userBids = await bidsApi.getUserBids();
            setBids(userBids);

            // Clear unread updates flag and refresh user state
            await authApi.clearUnreadUpdates();
            await refetchUser();
        } catch {
            setError('Failed to load bids. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [refetchUser]);

    useEffect(() => {
        // Fetch initial data
        fetchBids();

        const unsubscribe = on('bidStatusChanged', () => {
            fetchBids();
        });

        return () => {
            unsubscribe();
        };
    }, [fetchBids, on]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <p className="text-gray-600">{ tGeneral('loading') }</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center p-8 gap-4">
                <p className="text-error-600 text-center">{ error }</p>
                <Button color="secondary" onClick={ () => {
                    setIsLoading(true);
                    fetchBids();
                } }>
                    Try Again
                </Button>
            </div>
        );
    }

    if (bids.length === 0) {
        return <EmptyState
            className="overflow-hidden flex items-center justify-end"
            context="bids"
            actions={
                <Link href={ ROUTES.HOME }>
                    <Button color="secondary">
                        { tEmptyState('bids.browseCatalog') }
                    </Button>
                </Link>
            }
        />;
    }

    return (
        <div className="flex flex-col gap-4">
            { bids.map(bid => {
                const itemUrl = build.item(bid.auctionHouseSlug, bid.catalogId, String(bid.lotNumber));

                return <Link key={ `${ bid.catalogId }-${ bid.lotNumber }` } href={ itemUrl } className={ cx(
                    "block border border-gray-200 rounded-lg p-4",
                    "hover:border-gray-300 hover:shadow-md transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500") }>
                    {/* Status Badge */ }
                    <div className="flex items-center justify-between">
                        { bid.isWinning ?
                            <BadgeWithDot type="pill-color" color="success">{ tBids('winning') }</BadgeWithDot>
                            :
                            <BadgeWithDot type="pill-color" color="error">{ tBids('outbid') }</BadgeWithDot>
                        }
                        <p className="capitalize underline text-gray-400">{ bid.auctionHouseSlug }</p>
                    </div>

                    {/* Image Title & Name */ }
                    <div className="flex items-start gap-4 my-4">
                        <img src={ bid.imageUrl } alt={ bid.name }
                             className="w-16 h-16 bg-gray-100 rounded flex-shrink-0"/>
                        <div className="flex-1">
                            <p className="text-sm text-gray-600">
                                { tBids('lot') } { bid.lotNumber }
                            </p>
                            <p className="text-gray-900 line-clamp-2">{ bid.name }</p>
                        </div>
                    </div>

                    {/* Pricing */ }
                    <div className="flex">
                        <div className="flex-1">
                            <p className="text-xs text-gray-500">{ tBids('currentPrice') }</p>
                            <p className="text-lg text-gray-900">${ bid.currentPrice.toLocaleString() }</p>
                        </div>
                        <div className="text-end">
                            <p className="text-xs text-gray-500">{ tBids(bid.myMaxBid ? 'myMaxBid' : 'myBid') }</p>
                            <p className={ "text-lg font-bold " + (bid.isWinning ? 'text-success-500' : 'text-error-500') }>
                                ${ bid.myMaxBid ? bid.myMaxBid.toLocaleString() : bid.myBid.toLocaleString() }
                            </p>
                        </div>
                    </div>
                </Link>
            }) }
        </div>
    );
};
